import { match } from "ts-pattern";
import { z } from "zod";

interface FastApiError {
  detail?: unknown;
  details?: unknown;
  message?: unknown;
}

interface MessageApiError {
  kind: "message";
  message: string;
}

interface NativeApiError {
  error: Error;
  kind: "native-error";
}

interface ApiErrorList {
  errors: unknown[];
  kind: "list";
}

interface ParsedFastApiError {
  error: FastApiError;
  kind: "fast-api";
}

export interface ApiRequestError extends Error {
  name: "ApiRequestError";
  method: string;
  pathname: string;
  payload: unknown;
  status: number;
  statusText: string;
}

interface NormalizeApiErrorResponseOptions {
  request: RequestInfo | URL;
  requestOptions?: RequestInit;
  response: Response;
}

const fastApiValidationErrorSchema = z.object({
  loc: z.array(z.union([z.string(), z.number()])).catch([]),
  msg: z.string(),
});

const apiErrorSchema = z.union([
  z
    .string()
    .transform(
      (message) => ({ kind: "message", message }) satisfies MessageApiError,
    ),
  z
    .instanceof(Error)
    .refine((error) => Boolean(error.message))
    .transform(
      (error) => ({ error, kind: "native-error" }) satisfies NativeApiError,
    ),
  z
    .array(z.unknown())
    .transform((errors) => ({ errors, kind: "list" }) satisfies ApiErrorList),
  z
    .object({
      detail: z.unknown().optional(),
      details: z.unknown().optional(),
      message: z.unknown().optional(),
    })
    .transform(
      (error) => ({ error, kind: "fast-api" }) satisfies ParsedFastApiError,
    ),
]);

const throwingRequestOptionsSchema = z.object({
  method: z.string().optional(),
  throwOnError: z.literal(true),
});

const errorPayloadSchema = z.record(z.string(), z.unknown());

const reservedApiErrorProperties = new Set([
  "cause",
  "message",
  "method",
  "name",
  "pathname",
  "payload",
  "stack",
  "status",
  "statusText",
]);

const apiErrorStatusSchema = z.object({ status: z.int() });

const apiRequestErrorContextSchema = z.object({
  method: z.string(),
  pathname: z.string(),
  status: z.int(),
  statusText: z.string(),
});

export function getApiErrorStatus(error: unknown): number | null {
  const parsedError = apiErrorStatusSchema.safeParse(error);

  return parsedError.success ? parsedError.data.status : null;
}

export function getApiRequestErrorContext(error: unknown) {
  const parsedError = apiRequestErrorContextSchema.safeParse(error);

  return parsedError.success ? parsedError.data : null;
}

export function getApiErrorMessage(error: unknown): string | undefined {
  const parsedError = apiErrorSchema.safeParse(error);

  if (!parsedError.success) {
    return undefined;
  }

  return match(parsedError.data)
    .with({ kind: "message" }, ({ message }) => message)
    .with({ kind: "native-error" }, ({ error: nativeError }) =>
      getApiErrorMessage(nativeError.message),
    )
    .with({ kind: "list" }, ({ errors }) => {
      const messages = errors
        .map((item) => {
          const validationError = fastApiValidationErrorSchema.safeParse(item);

          if (!validationError.success) {
            return getApiErrorMessage(item);
          }

          const location = validationError.data.loc.join(".");
          return location
            ? `${location}: ${validationError.data.msg}`
            : validationError.data.msg;
        })
        .filter((message): message is string => typeof message === "string");

      return messages.length > 0 ? messages.join("; ") : undefined;
    })
    .with({ kind: "fast-api" }, ({ error: fastApiError }) =>
      [fastApiError.detail, fastApiError.details, fastApiError.message]
        .map(getApiErrorMessage)
        .find(Boolean),
    )
    .exhaustive();
}

export async function normalizeApiErrorResponse({
  request,
  requestOptions,
  response,
}: NormalizeApiErrorResponseOptions): Promise<Response> {
  const parsedRequestOptions =
    throwingRequestOptionsSchema.safeParse(requestOptions);

  if (response.ok || !parsedRequestOptions.success) {
    return response;
  }

  const responseText = await response.clone().text();
  let payload: unknown = responseText || undefined;

  if (responseText) {
    try {
      payload = JSON.parse(responseText);
    } catch {}
  }

  const method = (
    parsedRequestOptions.data.method
      ? parsedRequestOptions.data.method
      : request instanceof Request
        ? request.method
        : "GET"
  ).toUpperCase();
  const requestUrl =
    request instanceof Request ? request.url : request.toString();
  const pathname = new URL(requestUrl, "http://localhost").pathname;
  const statusDescription = [response.status, response.statusText]
    .filter(Boolean)
    .join(" ");
  const apiErrorMessage =
    getApiErrorMessage(payload) || response.statusText || "API request failed";
  const message =
    process.env.NODE_ENV === "development"
      ? `${apiErrorMessage} — ${method} ${pathname} returned ${statusDescription}`
      : apiErrorMessage;
  const parsedErrorPayload = errorPayloadSchema.safeParse(payload);
  const compatiblePayload = parsedErrorPayload.success
    ? Object.fromEntries(
        Object.entries(parsedErrorPayload.data).filter(
          ([property]) => !reservedApiErrorProperties.has(property),
        ),
      )
    : {};
  const metadata = {
    name: "ApiRequestError",
    method,
    pathname,
    payload,
    status: response.status,
    statusText: response.statusText,
  } satisfies Pick<
    ApiRequestError,
    "name" | "method" | "pathname" | "payload" | "status" | "statusText"
  >;

  throw Object.assign(
    new Error(message, {
      cause: payload,
    }),
    compatiblePayload,
    metadata,
  );
}
